import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import SEOHead from '../../components/SEOHead';


const treatmentsList = [
  { id: 'dental-implants', name: 'Dental Implants' },
  { id: 'porcelain-veneers', name: 'Porcelain Veneers' },
  { id: 'smile-makeover', name: 'Complete Smile Makeover' },
  { id: 'all-on-4', name: 'All-on-4 Implants' },
  { id: 'orthodontics', name: 'Orthodontics' },
  { id: 'teeth-whitening', name: 'Teeth Whitening' },
  { id: 'crowns-bridges', name: 'Crowns & Bridges' },
  { id: 'root-canal', name: 'Root Canal Treatment' },
];

const supervisionOptions = [
  { id: 'third-party', title: 'Independent Third-Party Doctor', description: 'An independent doctor with no clinic affiliation reviews your X-rays, scans, treatment plan, and final results', badge: 'Most Trusted', icon: 'ri-shield-check-line' },
  { id: 'own-doctor', title: 'Your Own Doctor Back Home', description: 'Your dentist stays involved, receives all records, and provides oversight throughout your treatment', badge: 'Stay Connected', icon: 'ri-user-heart-line' },
  { id: 'our-expert', title: 'Our In-House Expert', description: 'Choose one of our experienced specialists to supervise your entire treatment process', badge: 'No Extra Cost', icon: 'ri-star-line' },
];

export default function BookTreatmentPage() {
  const [searchParams] = useSearchParams();
  const treatmentParam = searchParams.get('treatment');
  const supervisionParam = searchParams.get('supervision');

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    selectedTreatments: treatmentParam ? [treatmentParam] : [] as string[],
    preferredDate: '',
    medicalHistory: '',
    supervisionOption: supervisionParam || '',
    doctorName: '',
    doctorEmail: '',
    doctorPhone: '',
    doctorSpecialty: '',
    doctorCountry: '',
    additionalNotes: '',
  });

  const [files, setFiles] = useState<{ xrays: File[]; photos: File[] }>({ xrays: [], photos: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const siteUrl = import.meta.env.VITE_SITE_URL || '';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTreatmentToggle = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedTreatments: prev.selectedTreatments.includes(id)
        ? prev.selectedTreatments.filter((t) => t !== id)
        : [...prev.selectedTreatments, id],
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'xrays' | 'photos') => {
    if (e.target.files) setFiles((prev) => ({ ...prev, [type]: Array.from(e.target.files!) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const submitData = new URLSearchParams();
      submitData.append('fullName', formData.fullName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('country', formData.country);
      submitData.append('selectedTreatments', formData.selectedTreatments.join(', '));
      submitData.append('preferredDate', formData.preferredDate);
      submitData.append('medicalHistory', formData.medicalHistory);
      submitData.append('supervisionOption', formData.supervisionOption);
      if (formData.supervisionOption === 'own-doctor') {
        submitData.append('doctorName', formData.doctorName);
        submitData.append('doctorEmail', formData.doctorEmail);
        submitData.append('doctorPhone', formData.doctorPhone);
        submitData.append('doctorSpecialty', formData.doctorSpecialty);
        submitData.append('doctorCountry', formData.doctorCountry);
      }
      submitData.append('additionalNotes', formData.additionalNotes);
      submitData.append('xraysUploaded', files.xrays.length > 0 ? 'Yes' : 'No');
      submitData.append('photosUploaded', files.photos.length > 0 ? 'Yes' : 'No');

      const response = await fetch('https://readdy.ai/api/form/d6eb4biohb161tfd8vsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: submitData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ fullName: '', email: '', phone: '', country: '', selectedTreatments: [], preferredDate: '', medicalHistory: '', supervisionOption: '', doctorName: '', doctorEmail: '', doctorPhone: '', doctorSpecialty: '', doctorCountry: '', additionalNotes: '' });
        setFiles({ xrays: [], photos: [] });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${siteUrl}/book-treatment`,
        url: `${siteUrl}/book-treatment`,
        name: 'Book Your Dental Treatment in Turkey — Plexus Alliance',
        description: 'Book your dental treatment in Istanbul, Turkey with full doctor supervision. Choose from implants, veneers, smile makeovers & more. Save 60–70%, free consultation, response within 24 hours.',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Book Treatment', item: `${siteUrl}/book-treatment` },
          ],
        },
      },
      {
        '@type': 'MedicalBusiness',
        name: 'Plexus Alliance',
        url: siteUrl,
        description: 'Doctor-supervised dental tourism in Istanbul, Turkey. Save up to 70% on dental implants, veneers, smile makeovers and more.',
        telephone: '+90-531-614-8445',
        email: 'info@plexusalliance.com',
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Istanbul',
          addressCountry: 'TR',
        },
        potentialAction: {
          '@type': 'ReserveAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${siteUrl}/book-treatment`,
            actionPlatform: ['https://schema.org/DesktopWebPlatform', 'https://schema.org/MobileWebPlatform'],
          },
          result: {
            '@type': 'Reservation',
            name: 'Dental Treatment Consultation Booking',
          },
        },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEOHead
        title="Book Your Dental Treatment in Turkey | Free Consultation — Plexus Alliance"
        description="Book your dental treatment in Istanbul, Turkey with full doctor supervision. Choose from implants, veneers, smile makeovers & more. Save 60–70%, free consultation, response within 24 hours."
        keywords="book dental treatment Turkey, dental appointment Istanbul, dental consultation Turkey"
        canonical={`${siteUrl}/book-treatment`}
        schema={schema}
      />
      <Navigation />

      {/* ── HERO ── */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/e157114bfe7fdb60525463293556f069.jpg"
            alt="Luxury dental clinic"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-[#0a0a0a]" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-12 h-px bg-amber-400/60" />
            <span className="text-amber-400/80 text-xs font-medium tracking-[0.3em] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
              Book Your Treatment
            </span>
            <div className="w-12 h-px bg-amber-400/60" />
          </div>
          <h1
            className="text-4xl md:text-6xl font-normal text-white mb-4 leading-[1.05]"
            style={{ fontFamily: "'Playfair Display', serif", letterSpacing: '-0.01em' }}
          >
            Start Your
            <br />
            <em className="text-amber-300 not-italic">Dental Journey.</em>
          </h1>
          <p className="text-white/40 text-base font-light" style={{ fontFamily: "'Inter', sans-serif" }}>
            Fill out the form below and our team will contact you within 24 hours.
          </p>
        </div>
      </section>

      {/* ── FORM ── */}
      <section className="pb-24 px-6 md:px-10">
        <div className="max-w-4xl mx-auto">

          {/* Success */}
          {submitStatus === 'success' && (
            <div className="border border-amber-400/20 bg-amber-400/5 p-8 mb-8 text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 border border-amber-400/30">
                <i className="ri-check-line text-2xl text-amber-400" />
              </div>
              <h3 className="text-xl font-normal text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Booking Submitted Successfully!</h3>
              <p className="text-white/40 text-sm font-light mb-5" style={{ fontFamily: "'Inter', sans-serif" }}>
                Thank you for choosing us. Our team will review your information and contact you within 24 hours.
              </p>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="bg-amber-400 hover:bg-amber-300 text-black px-7 py-3 font-semibold text-sm tracking-wide transition-all cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Return to Home
              </button>
            </div>
          )}

          {/* Error */}
          {submitStatus === 'error' && (
            <div className="border border-red-500/20 bg-red-500/5 p-5 mb-8 flex items-center gap-3">
              <i className="ri-error-warning-line text-red-400 text-xl" />
              <div>
                <p className="text-red-400 text-sm font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>Submission Failed</p>
                <p className="text-white/30 text-xs font-light" style={{ fontFamily: "'Inter', sans-serif" }}>Please try again or contact us directly.</p>
              </div>
            </div>
          )}

          <form id="booking-form" data-readdy-form onSubmit={handleSubmit} className="border border-white/5 bg-[#0f0f0f]">

            {/* Section helper */}
            {([
              {
                icon: 'ri-user-line',
                title: 'Personal Information',
                content: (
                  <div className="grid md:grid-cols-2 gap-5">
                    {[
                      { label: 'Full Name', name: 'fullName', type: 'text', placeholder: 'John Doe', required: true },
                      { label: 'Email', name: 'email', type: 'email', placeholder: 'john@example.com', required: true },
                      { label: 'Phone Number', name: 'phone', type: 'tel', placeholder: '+1 234 567 8900', required: true },
                      { label: 'Country', name: 'country', type: 'text', placeholder: 'United States', required: true },
                    ].map((field) => (
                      <div key={field.name}>
                        <label className="block text-xs font-medium text-white/30 tracking-widest uppercase mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {field.label} {field.required && <span className="text-amber-400">*</span>}
                        </label>
                        <input
                          type={field.type}
                          name={field.name}
                          value={(formData as Record<string, string>)[field.name]}
                          onChange={handleInputChange}
                          required={field.required}
                          placeholder={field.placeholder}
                          className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 text-white/70 text-sm font-light focus:border-amber-400/40 focus:outline-none transition-colors placeholder:text-white/15"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        />
                      </div>
                    ))}
                  </div>
                ),
              },
            ] as { icon: string; title: string; content: React.ReactNode }[]).map((section, i) => (
              <div key={i} className="p-8 md:p-10 border-b border-white/5">
                <h2 className="flex items-center gap-3 text-base font-medium text-white/60 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                  <i className={`${section.icon} text-amber-400/60`} />
                  {section.title}
                </h2>
                {section.content}
              </div>
            ))}

            {/* Treatment Selection */}
            <div className="p-8 md:p-10 border-b border-white/5">
              <h2 className="flex items-center gap-3 text-base font-medium text-white/60 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                <i className="ri-tooth-line text-amber-400/60" />
                Select Treatments <span className="text-amber-400 text-xs ml-1">*</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-2">
                {treatmentsList.map((treatment) => {
                  const selected = formData.selectedTreatments.includes(treatment.id);
                  return (
                    <div
                      key={treatment.id}
                      onClick={() => handleTreatmentToggle(treatment.id)}
                      className={`p-4 border cursor-pointer transition-all ${selected ? 'border-amber-400/40 bg-amber-400/5' : 'border-white/5 hover:border-white/15'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${selected ? 'border-amber-400 bg-amber-400' : 'border-white/20'}`}>
                          {selected && <i className="ri-check-line text-black text-xs" />}
                        </div>
                        <span className="text-sm font-light text-white/60" style={{ fontFamily: "'Inter', sans-serif" }}>{treatment.name}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Preferred Date */}
            <div className="p-8 md:p-10 border-b border-white/5">
              <h2 className="flex items-center gap-3 text-base font-medium text-white/60 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                <i className="ri-calendar-line text-amber-400/60" />
                Preferred Date
              </h2>
              <input
                type="date"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleInputChange}
                className="w-full md:w-64 px-4 py-3 bg-[#0a0a0a] border border-white/10 text-white/70 text-sm font-light focus:border-amber-400/40 focus:outline-none transition-colors"
                style={{ fontFamily: "'Inter', sans-serif" }}
              />
            </div>

            {/* Medical History */}
            <div className="p-8 md:p-10 border-b border-white/5">
              <h2 className="flex items-center gap-3 text-base font-medium text-white/60 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                <i className="ri-file-list-line text-amber-400/60" />
                Medical History
              </h2>
              <textarea
                name="medicalHistory"
                value={formData.medicalHistory}
                onChange={handleInputChange}
                maxLength={500}
                rows={4}
                placeholder="Please list any medical conditions, allergies, or medications you're currently taking..."
                className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 text-white/70 text-sm font-light focus:border-amber-400/40 focus:outline-none transition-colors resize-none placeholder:text-white/15"
                style={{ fontFamily: "'Inter', sans-serif" }}
              />
              <div className="text-xs text-white/20 mt-2 text-right" style={{ fontFamily: "'Inter', sans-serif" }}>{formData.medicalHistory.length}/500</div>
            </div>

            {/* File Uploads */}
            <div className="p-8 md:p-10 border-b border-white/5">
              <h2 className="flex items-center gap-3 text-base font-medium text-white/60 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                <i className="ri-image-line text-amber-400/60" />
                Upload Files <span className="text-white/20 text-xs font-light ml-1">(Optional)</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { label: 'Dental X-rays', type: 'xrays' as const, id: 'xrays-upload', icon: 'ri-file-upload-line', accept: 'image/*,.pdf' },
                  { label: 'Dental Photos', type: 'photos' as const, id: 'photos-upload', icon: 'ri-image-add-line', accept: 'image/*' },
                ].map((upload) => (
                  <div key={upload.type}>
                    <label className="block text-xs font-medium text-white/25 tracking-widest uppercase mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>{upload.label}</label>
                    <div className="border border-dashed border-white/10 p-6 text-center hover:border-amber-400/20 transition-colors">
                      <i className={`${upload.icon} text-2xl text-white/15 mb-2`} />
                      <p className="text-xs text-white/20 mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>Uncollectable</p>
                      <input type="file" onChange={(e) => handleFileChange(e, upload.type)} multiple accept={upload.accept} className="hidden" id={upload.id} />
                      <label htmlFor={upload.id} className="inline-block px-4 py-2 border border-white/10 text-white/30 text-xs font-medium cursor-pointer hover:border-white/20 hover:text-white/50 transition-colors" style={{ fontFamily: "'Inter', sans-serif" }}>
                        Choose Files
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Supervision Options */}
            <div className="p-8 md:p-10 border-b border-white/5">
              <h2 className="flex items-center gap-3 text-base font-medium text-white/60 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                <i className="ri-shield-check-line text-amber-400/60" />
                Choose Supervision Option <span className="text-amber-400 text-xs ml-1">*</span>
              </h2>
              <div className="space-y-2">
                {supervisionOptions.map((option) => {
                  const selected = formData.supervisionOption === option.id;
                  return (
                    <div
                      key={option.id}
                      onClick={() => setFormData((prev) => ({ ...prev, supervisionOption: option.id }))}
                      className={`p-5 border cursor-pointer transition-all ${selected ? 'border-amber-400/40 bg-amber-400/5' : 'border-white/5 hover:border-white/15'}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center mt-0.5 flex-shrink-0 ${selected ? 'border-amber-400' : 'border-white/20'}`}>
                          {selected && <div className="w-2 h-2 rounded-full bg-amber-400" />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <i className={`${option.icon} text-amber-400/60 text-sm`} />
                              <span className="text-sm font-medium text-white/70" style={{ fontFamily: "'Inter', sans-serif" }}>{option.title}</span>
                            </div>
                            <span className="text-[10px] font-medium text-amber-400 border border-amber-400/20 bg-amber-400/5 px-2 py-0.5 whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif" }}>{option.badge}</span>
                          </div>
                          <p className="text-xs text-white/25 font-light" style={{ fontFamily: "'Inter', sans-serif" }}>{option.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Own Doctor Details */}
            {formData.supervisionOption === 'own-doctor' && (
              <div className="p-8 md:p-10 border-b border-white/5 bg-amber-400/[0.02]">
                <h3 className="flex items-center gap-3 text-base font-medium text-white/60 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                  <i className="ri-stethoscope-line text-amber-400/60" />
                  Your Doctor's Information
                </h3>
                <div className="grid md:grid-cols-2 gap-5">
                  {[
                    { label: "Doctor's Name", name: 'doctorName', type: 'text', placeholder: 'Dr. Smith' },
                    { label: "Doctor's Email", name: 'doctorEmail', type: 'email', placeholder: 'doctor@clinic.com' },
                    { label: "Doctor's Phone", name: 'doctorPhone', type: 'tel', placeholder: '+1 234 567 8900' },
                    { label: 'Specialty', name: 'doctorSpecialty', type: 'text', placeholder: 'General Dentistry' },
                  ].map((field) => (
                    <div key={field.name}>
                      <label className="block text-xs font-medium text-white/25 tracking-widest uppercase mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                        {field.label} <span className="text-amber-400">*</span>
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        value={(formData as Record<string, string>)[field.name]}
                        onChange={handleInputChange}
                        required
                        placeholder={field.placeholder}
                        className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 text-white/70 text-sm font-light focus:border-amber-400/40 focus:outline-none transition-colors placeholder:text-white/15"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-white/25 tracking-widest uppercase mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Doctor's Country <span className="text-amber-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="doctorCountry"
                      value={formData.doctorCountry}
                      onChange={handleInputChange}
                      required
                      placeholder="United States"
                      className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 text-white/70 text-sm font-light focus:border-amber-400/40 focus:outline-none transition-colors placeholder:text-white/15"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Additional Notes */}
            <div className="p-8 md:p-10 border-b border-white/5">
              <h2 className="flex items-center gap-3 text-base font-medium text-white/60 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                <i className="ri-chat-3-line text-amber-400/60" />
                Additional Notes
              </h2>
              <textarea
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleInputChange}
                maxLength={500}
                rows={4}
                placeholder="Any special requests or questions..."
                className="w-full px-4 py-3 bg-[#0a0a0a] border border-white/10 text-white/70 text-sm font-light focus:border-amber-400/40 focus:outline-none transition-colors resize-none placeholder:text-white/15"
                style={{ fontFamily: "'Inter', sans-serif" }}
              />
              <div className="text-xs text-white/20 mt-2 text-right" style={{ fontFamily: "'Inter', sans-serif" }}>{formData.additionalNotes.length}/500</div>
            </div>

            {/* Submit */}
            <div className="p-8 md:p-10 text-center">
              <button
                type="submit"
                disabled={isSubmitting || formData.selectedTreatments.length === 0 || !formData.supervisionOption}
                className="bg-amber-400 hover:bg-amber-300 text-black px-12 py-4 font-semibold text-sm tracking-wide transition-all hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {isSubmitting ? (
                  <><i className="ri-loader-4-line animate-spin mr-2" />Submitting...</>
                ) : (
                  <><i className="ri-send-plane-fill mr-2" />Submit Booking Request</>
                )}
              </button>
              <p className="text-xs text-white/20 mt-4 font-light" style={{ fontFamily: "'Inter', sans-serif" }}>
                We'll contact you within 24 hours to confirm your booking
              </p>
            </div>
          </form>
        </div>
      </section>

      <Footer onBookClick={() => window.REACT_APP_NAVIGATE('/book-treatment')} />
    </div>
  );
}
